import camelCase from "lodash/camelCase";
import snakeCase from "lodash/snakeCase";

import labels from "../../../constants/displayFields";
import { getCostCenterValue } from "../../../views/capture/FieldsIdentifiers";
import { getCustomDescriptionValue } from "../../../utils/asset";

export const DEFAULT_STORE_CARD_FIELD_KEYS = [
  "description",
  "dimensions",
  "warehouse_name",
  "available_quantity",
];

/**
 * Reading order matching the legacy store card (`AssetCardTitle` + project, warehouse, cost center, quantities).
 * Filtered against enabled project fields when building a custom layout (see `buildLegacyStoreCardOrderedFields`).
 */
export const LEGACY_STORE_CARD_SEED_KEYS = [
  "sku_number",
  "manufacturer",
  "description",
  "model",
  "dimensions",
  "po_number",
  "color",
  "condition",
  "description_comments",
  "expiration_month_day_year",
  "warehouse_name",
  "id_cost_center",
];

/**
 * @param {Array<[string, string, string]>} availableFields from `useStoreCardFieldsRules`
 * @returns {string[]} ordered field keys to persist as `storeCardFields.ordered_fields`
 */
export function buildLegacyStoreCardOrderedFields(availableFields) {
  if (!availableFields?.length) return [];
  const availableSet = new Set(availableFields.map(field => field[0]));
  return LEGACY_STORE_CARD_SEED_KEYS.filter(key => availableSet.has(key));
}

const isNA = value => {
  if (!value) return false;
  const normalized = String(value).trim().toLowerCase().replace(/\./g, "").replace(/\//g, "");
  return normalized === "na";
};

const notNA = value => value && !isNA(value);

const getDimension = (value = []) =>
  value
    .filter(v => v.width || v.depth || v.height)
    .map(field =>
      [`${field.width}\u2033\u00A0W`, `${field.depth}\u2033\u00A0D`, `${field.height}\u2033\u00A0H`].join(
        "\u00A0x\u00A0"
      )
    )
    .join(", ");

const getExpiration = asset => {
  if (asset.expirationMonthDayYear) return asset.expirationMonthDayYear;
  if (asset.expirationDate) return asset.expirationDate;
  if (asset.expirationMonth && asset.expirationYear) {
    return `${asset.expirationMonth} - ${asset.expirationYear}`;
  }
  return null;
};

/**
 * Matches Item Details "Inbound Summary" / export `associated_inbound_shipments`:
 * `shipmentId (quantity)` per shipment line, multiple lines joined with "; ".
 */
const getInboundSummaryDisplay = asset => {
  const fromShipments = (asset?.shipmentItems || [])
    .map(item => {
      const shipmentId = item?.shipment?.shipmentId;
      if (!shipmentId) return null;
      const qty = item?.quantity;
      if (qty == null) return String(shipmentId);
      return `${shipmentId} (${qty})`;
    })
    .filter(Boolean)
    .join("; ");

  if (fromShipments) return fromShipments;

  const raw = asset?.receivingReportNumber ?? asset?.receiving_report_number;
  if (raw == null || String(raw).trim() === "") return null;
  return String(raw);
};

const getDisplayDescription = asset => {
  const description = asset?.description;
  if (description && notNA(description)) return description;

  const customValue = getCustomDescriptionValue(asset);
  return customValue && notNA(customValue) ? customValue : "";
};

const getAssetValue = (asset, fieldKey) => {
  if (!asset) return null;

  const camelKey = camelCase(fieldKey);
  if (asset[camelKey] != null) return asset[camelKey];

  const snakeKey = snakeCase(fieldKey);
  if (asset[snakeKey] != null) return asset[snakeKey];

  return null;
};

const getCustomFieldValue = (asset, fieldKey) => {
  if (!fieldKey.startsWith("custom_field_")) return null;

  const templateId = fieldKey.replace("custom_field_", "");
  return (
    asset.customFields?.find(field => String(field.customFieldTemplate?.id) === templateId)?.value || null
  );
};

const buildFieldLabel = fieldKey => labels[fieldKey] || labels[snakeCase(fieldKey)] || fieldKey;

export function getStoreCardFieldValue({
  asset,
  fieldKey,
  displayClientProject,
  displayWarehouse,
  reservationsEnabled,
}) {
  switch (fieldKey) {
    case "description":
      return getDisplayDescription(asset);
    case "dimensions":
      return getDimension(asset.dimensions);
    case "warehouse_name":
      if (!displayWarehouse) return null;
      return asset.warehousesFromRackLocations?.map(warehouse => warehouse.name).join(", ") || null;
    case "project_name":
    case "filter_field2_name":
      return displayClientProject ? asset.project?.name : null;
    case "available_quantity":
      return `${asset.availableQuantity} Available`;
    case "reserved_quantity":
      return reservationsEnabled && Number(asset.reservedQuantity || 0) !== 0
        ? `${asset.reservedQuantity} Reserved`
        : null;
    case "id_cost_center":
    case "cost_center":
      return asset.costCenter ? getCostCenterValue(asset.costCenter) : null;
    case "expiration_date":
    case "expiration_month":
    case "expiration_year":
    case "expiration_month_day_year":
      return getExpiration(asset);
    case "rack_locations":
      return asset.rackLocations?.join?.(", ") || null;
    case "receiving_report_number":
      return getInboundSummaryDisplay(asset);
    default: {
      if (fieldKey.startsWith("custom_field_")) {
        return getCustomFieldValue(asset, fieldKey);
      }

      const rawValue = getAssetValue(asset, fieldKey);
      if (Array.isArray(rawValue)) {
        return rawValue.join(", ");
      }
      return rawValue;
    }
  }
}

export function getStoreCardFieldRows({
  asset,
  orderedFieldKeys,
  displayClientProject,
  displayWarehouse,
  reservationsEnabled,
}) {
  const fieldKeys =
    orderedFieldKeys && orderedFieldKeys.length > 0
      ? orderedFieldKeys
      : DEFAULT_STORE_CARD_FIELD_KEYS;

  return fieldKeys
    .map(fieldKey => ({
      fieldKey,
      label: buildFieldLabel(fieldKey),
      value: getStoreCardFieldValue({
        asset,
        fieldKey,
        displayClientProject,
        displayWarehouse,
        reservationsEnabled,
      }),
    }))
    .filter(row => notNA(row.value));
}

export { getDisplayDescription, getDimension, getExpiration, isNA, notNA };
